import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, formatErr } from "@/context/AuthContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Bank, CreditCard, Plus, PencilSimple, TrashSimple, Check, X,
} from "@phosphor-icons/react";

const EMPTY = {
  type: "bank_account",
  label: "",
  bank_name: "",
  branch: "",
  account_number: "",
  iban: "",
  currency: "TL",
  card_number: "",
  card_holder: "",
  is_active: true,
  note: "",
};

/**
 * Hesaplar / Bankalar (Faz 34.55)
 * Banka hesapları ve kredi kartlarının tanımlandığı sayfa. Gider/Gelir
 * formunun "Ödeme Yapılan Hesap" dropdown'ının kaynağıdır.
 */
export default function BudgetAccounts() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all"); // all | bank_account | credit_card
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/accounts`);
      setRows(data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = (type = "bank_account") => {
    setEditing(null);
    setForm({ ...EMPTY, type });
    setOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      type: r.type,
      label: r.label || "",
      bank_name: r.bank_name || "",
      branch: r.branch || "",
      account_number: r.account_number || "",
      iban: r.iban || "",
      currency: r.currency || "TL",
      card_number: "", // güvenlik: eski numara maskeli, düzenlerken boş bırak
      card_holder: r.card_holder || "",
      is_active: r.is_active !== false,
      note: r.note || "",
    });
    setOpen(true);
  };
  const save = async () => {
    if (!form.label?.trim()) { toast.error("Etiket zorunlu"); return; }
    if (!form.bank_name?.trim()) { toast.error("Banka adı zorunlu"); return; }
    if (form.type === "bank_account" && !form.account_number?.trim() && !form.iban?.trim()) {
      toast.error("Hesap Numarası veya IBAN'dan en az biri zorunlu");
      return;
    }
    if (form.type === "credit_card" && !editing && !form.card_number?.trim()) {
      toast.error("Kart numarası zorunlu");
      return;
    }
    try {
      if (editing) {
        await axios.put(`${API}/accounts/${editing.id}`, form);
        toast.success("Hesap güncellendi");
      } else {
        await axios.post(`${API}/accounts`, form);
        toast.success("Hesap eklendi");
      }
      setOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const remove = async (r) => {
    if (!window.confirm(`"${r.label}" hesabını silmek istediğinize emin misiniz?`)) return;
    try {
      await axios.delete(`${API}/accounts/${r.id}`);
      toast.success("Silindi");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const filtered = rows.filter((r) => {
    if (filter !== "all" && r.type !== filter) return false;
    if (q) {
      const nq = q.toLowerCase();
      return (r.label || "").toLowerCase().includes(nq)
          || (r.bank_name || "").toLowerCase().includes(nq)
          || (r.iban || "").toLowerCase().includes(nq)
          || (r.account_number || "").toLowerCase().includes(nq);
    }
    return true;
  });

  const banks = rows.filter((r) => r.type === "bank_account").length;
  const cards = rows.filter((r) => r.type === "credit_card").length;

  return (
    <Shell title="Hesaplar / Bankalar" subtitle="Bütçe Yönetimi">
      <div className="grid grid-cols-2 gap-3 mb-3">
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#F0F5FA] border border-[#1B4776]/20">
              <Bank size={22} weight="duotone" className="text-[#1B4776]" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Banka Hesapları</div>
              <div className="text-2xl font-bold text-[#1B4776]">{banks}</div>
            </div>
          </div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#FFF7E6] border border-[#95C11F]/40">
              <CreditCard size={22} weight="duotone" className="text-[#556B10]" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Kredi Kartları</div>
              <div className="text-2xl font-bold text-[#556B10]">{cards}</div>
            </div>
          </div>
        </Card>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Input placeholder="Etiket, banka veya IBAN ara..." value={q}
               onChange={(e) => setQ(e.target.value)}
               className="rounded-none sm:rounded-sm max-w-xs h-9"
               data-testid="acc-search" />
        <div className="flex gap-1">
          {[
            { v: "all", l: "Tümü" },
            { v: "bank_account", l: "Banka" },
            { v: "credit_card", l: "Kredi Kartı" },
          ].map((o) => (
            <button key={o.v} type="button" onClick={() => setFilter(o.v)}
                    data-testid={`acc-filter-${o.v}`}
                    className={`h-9 px-3 text-xs uppercase tracking-wider font-bold border transition-colors ${
                      filter === o.v
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100"
                    }`}>
              {o.l}
            </button>
          ))}
        </div>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={() => openNew("bank_account")}
                  className="rounded-none sm:rounded-sm h-9"
                  data-testid="acc-add-bank">
            <Bank size={14} weight="bold" className="mr-1" /> Banka Hesabı
          </Button>
          <Button onClick={() => openNew("credit_card")}
                  className="bg-[#1B4776] hover:bg-[#12324f] text-white rounded-none sm:rounded-sm h-9"
                  data-testid="acc-add-card">
            <CreditCard size={14} weight="bold" className="mr-1" /> Kredi Kartı
          </Button>
        </div>
      </div>

      <Card className="rounded-none sm:rounded-sm border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="border-b border-slate-200 text-[10px] uppercase tracking-widest font-bold text-slate-500">
              <th className="px-4 py-3 text-left w-32">Tip</th>
              <th className="px-4 py-3 text-left">Etiket</th>
              <th className="px-4 py-3 text-left">Banka</th>
              <th className="px-4 py-3 text-left">Hesap No / Kart</th>
              <th className="px-4 py-3 text-left">IBAN / Kart Sahibi</th>
              <th className="px-4 py-3 text-center w-20">Durum</th>
              <th className="px-4 py-3 text-right w-32">İşlem</th>
            </tr>
          </thead>
          <tbody data-testid="acc-list-body">
            {filtered.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-16 text-center text-slate-400">
                Kayıt yok. Yukarıdan yeni bir hesap ekleyin.
              </td></tr>
            ) : filtered.map((r) => {
              const isBank = r.type === "bank_account";
              return (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/70"
                    data-testid={`acc-row-${r.id}`}>
                  <td className="px-4 py-2.5">
                    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 border ${
                      isBank ? "bg-[#F0F5FA] text-[#1B4776] border-[#1B4776]/30"
                            : "bg-[#FFF7E6] text-[#556B10] border-[#95C11F]/40"
                    }`}>
                      {isBank ? <Bank size={10} weight="fill" /> : <CreditCard size={10} weight="fill" />}
                      {isBank ? "Banka" : "Kredi Kartı"}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 font-semibold text-slate-800">{r.label}</td>
                  <td className="px-4 py-2.5 text-slate-700">
                    {r.bank_name}
                    {r.branch && <span className="text-slate-400 ml-1">· {r.branch}</span>}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-600">
                    {isBank ? (r.account_number || "—") : (r.card_number_masked || "—")}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-600">
                    {isBank ? (r.iban || "—") : (r.card_holder || "—")}
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    {r.is_active
                      ? <Check size={16} weight="bold" className="inline text-emerald-600" />
                      : <X size={16} weight="bold" className="inline text-slate-400" />}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <button onClick={() => openEdit(r)}
                            className="p-1 text-slate-500 hover:text-[#1B4776]"
                            data-testid={`acc-edit-${r.id}`}>
                      <PencilSimple size={14} weight="bold" />
                    </button>
                    <button onClick={() => remove(r)}
                            className="p-1 text-slate-500 hover:text-rose-600"
                            data-testid={`acc-del-${r.id}`}>
                      <TrashSimple size={14} weight="bold" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {/* Hesap Ekle/Düzenle Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-none sm:rounded-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {form.type === "bank_account"
                ? <Bank size={18} weight="duotone" className="text-[#1B4776]" />
                : <CreditCard size={18} weight="duotone" className="text-[#556B10]" />}
              {editing ? "Hesabı Düzenle" : `Yeni ${form.type === "bank_account" ? "Banka Hesabı" : "Kredi Kartı"}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {!editing && (
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Tür</label>
                <Select value={form.type}
                        onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                  <SelectTrigger className="rounded-none sm:rounded-sm h-10" data-testid="acc-form-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_account">Banka Hesabı</SelectItem>
                    <SelectItem value="credit_card">Kredi Kartı</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Etiket *</label>
                <Input value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                       placeholder={form.type === "bank_account" ? "Ziraat Şantiye" : "YK Bonus"}
                       className="rounded-none sm:rounded-sm h-10"
                       data-testid="acc-form-label" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Banka Adı *</label>
                <Input value={form.bank_name} onChange={(e) => setForm((f) => ({ ...f, bank_name: e.target.value }))}
                       placeholder="Ziraat Bankası"
                       className="rounded-none sm:rounded-sm h-10"
                       data-testid="acc-form-bank" />
              </div>
            </div>

            {form.type === "bank_account" ? (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Şube</label>
                    <Input value={form.branch} onChange={(e) => setForm((f) => ({ ...f, branch: e.target.value }))}
                           className="rounded-none sm:rounded-sm h-10"
                           data-testid="acc-form-branch" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Para Birimi</label>
                    <Select value={form.currency}
                            onValueChange={(v) => setForm((f) => ({ ...f, currency: v }))}>
                      <SelectTrigger className="rounded-none sm:rounded-sm h-10" data-testid="acc-form-currency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="TL">TL — Türk Lirası</SelectItem>
                        <SelectItem value="USD">USD — Dolar</SelectItem>
                        <SelectItem value="EUR">EUR — Euro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Hesap Numarası</label>
                  <Input value={form.account_number}
                         onChange={(e) => setForm((f) => ({ ...f, account_number: e.target.value }))}
                         className="rounded-none sm:rounded-sm h-10 font-mono"
                         data-testid="acc-form-account" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">IBAN</label>
                  <Input value={form.iban}
                         onChange={(e) => setForm((f) => ({ ...f, iban: e.target.value.toUpperCase() }))}
                         placeholder="TR00 0000 0000 0000 0000 00"
                         className="rounded-none sm:rounded-sm h-10 font-mono"
                         data-testid="acc-form-iban" />
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Kart Sahibi</label>
                  <Input value={form.card_holder}
                         onChange={(e) => setForm((f) => ({ ...f, card_holder: e.target.value }))}
                         className="rounded-none sm:rounded-sm h-10"
                         data-testid="acc-form-holder" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                    Kart Numarası {editing && "(değiştirmek için yeniden girin, aksi halde boş bırakın)"}
                  </label>
                  <Input value={form.card_number}
                         onChange={(e) => setForm((f) => ({ ...f, card_number: e.target.value }))}
                         placeholder="0000 0000 0000 0000"
                         className="rounded-none sm:rounded-sm h-10 font-mono"
                         data-testid="acc-form-card" />
                  <div className="text-[10px] text-slate-400 mt-1">
                    Listede sadece son 4 hane görünür (**** **** **** ####).
                  </div>
                </div>
              </>
            )}

            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Not</label>
              <Input value={form.note}
                     onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                     className="rounded-none sm:rounded-sm h-10"
                     data-testid="acc-form-note" />
            </div>
            <label className="inline-flex items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" checked={form.is_active}
                     onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                     data-testid="acc-form-active" />
              Aktif (Gider/Gelir formunda seçilebilir)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}
                    className="rounded-none sm:rounded-sm">İptal</Button>
            <Button onClick={save}
                    className="bg-[#1B4776] hover:bg-[#12324f] text-white rounded-none sm:rounded-sm"
                    data-testid="acc-form-save">
              {editing ? "Güncelle" : "Ekle"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
